import type {EntitlementGrant, EntitlementPreset} from '../typedef';
import type {EntitlementsResource} from 'src/openapi/admin/models/EntitlementsResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {PresetItem} from 'src/openapi/admin/models/PresetItem';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';
import type {Role} from 'src/openapi/admin/models/Role';


type Props = {
    preset: PresetItem,
    relations: PresetRelationItem[],
    roles: Record<string, Role>,
    grants: Record<string, Grant>,
    resources: Record<string, EntitlementsResource>,
};

export const buildPresetDetails = ({preset, roles, relations, grants, resources}: Props): EntitlementPreset => {
    const result: EntitlementPreset = {
        ...preset,
        freeGrants: [],
        roles: [],
    };

    const roleIds = relations
        .filter(({roleId}) => roleId)
        .map(({roleId, level}) => ({roleId, level}))
        .reduce((acc, {roleId, level}) => {
            const existed = acc.find(item => item.roleId === roleId);
            if (existed) {
                return acc;
            }
            return [...acc, {roleId, level}];
        }, [] as {roleId: string | undefined, level: string}[]);

    roleIds.sort((a, b) => {
        const roleA = roles[a.roleId || ''];
        const roleB = roles[b.roleId || ''];
        return roleA.name.localeCompare(roleB.name, undefined, {numeric: true, sensitivity: 'base'});
    }).forEach(roleId => {
        const role = roles[roleId.roleId || ''];
        if (role) {
            const grantIds = relations
                .filter(rel => rel.roleId === roleId.roleId)
                .map(({grantId, level}) => ({grantId, level}))
                .reduce((acc, {grantId, level}) => {
                    const existed = acc.find(item => item.grantId === grantId);
                    if (existed) {
                        return acc;
                    }
                    return [...acc, {grantId, level}];
                }, [] as {grantId: string, level: string}[]);

            const rolesGrants: EntitlementGrant[] = [];

            grantIds.sort((a, b) => {
                const grantA = grants[a.grantId];
                const grantB = grants[b.grantId];
                return grantA.name.localeCompare(grantB.name, undefined, {numeric: true, sensitivity: 'base'});
            }).forEach(grantId => {
                const loadedGrant = grants[grantId.grantId];
                if (loadedGrant) {
                    const grantResourceIds = relations
                        .filter(rel => rel.grantId === grantId.grantId)
                        .sort((a, b) => {
                            const resourceA = resources[a.resourceId];
                            const resourceB = resources[b.resourceId];
                            return resourceA.name.localeCompare(resourceB.name, undefined, {
                                numeric: true,
                                sensitivity: 'base',
                            });
                        })
                        .reduce((acc, {resourceId, level}) => {
                            const existed = acc.find(item => item.resourceId === resourceId);
                            if (existed) {
                                return acc;
                            }
                            return [...acc, {resourceId, level}];
                        }, [] as {resourceId: string, level: string}[]);

                    const grantResources = grantResourceIds.map(({resourceId, level}) => ({
                        ...resources[resourceId],
                        level,
                    }));

                    rolesGrants.push({
                        ...loadedGrant,
                        level: grantId.level,
                        resources: grantResources,
                    });
                }
            });

            result.roles.push({
                ...role,
                level: roleId.level,
                grants: rolesGrants,
            });
        }
    });

    const freeGrantIds = relations
        .filter(({roleId}) => !roleId)
        .map(({grantId, level}) => ({grantId, level}))
        .reduce((acc, {grantId, level}) => {
            const existed = acc.find(item => item.grantId === grantId);
            if (existed) {
                return acc;
            }
            return [...acc, {grantId, level}];
        }, [] as {grantId: string, level: string}[]);
    freeGrantIds.sort((a, b) => {
        const grantA = grants[a.grantId];
        const grantB = grants[b.grantId];
        return grantA.name.localeCompare(grantB.name, undefined, {numeric: true, sensitivity: 'base'});
    }).forEach(grantId => {
        const loadedGrant = grants[grantId.grantId];
        if (loadedGrant) {
            const grantResourceIds = relations
                .filter(rel => rel.grantId === grantId.grantId)
                .sort((a, b) => resources[a.resourceId].name.localeCompare(resources[b.resourceId].name))
                .reduce((acc, {resourceId, level}) => {
                    const existed = acc.find(item => item.resourceId === resourceId);
                    if (existed) {
                        return acc;
                    }
                    return [...acc, {resourceId, level}];
                }, [] as {resourceId: string, level: string}[]);

            const grantResources = grantResourceIds.map(({resourceId, level}) => ({
                ...resources[resourceId],
                level,
            }));

            result.freeGrants.push({
                ...loadedGrant,
                level: grantId.level,
                resources: grantResources,
            });
        }
    });

    return result;
};
